/**
 * Role-Based Access Control (RBAC) Utilities for VerveAI Launchpad (verveai.co)
 * Roles:
 * - USER: Standard signed-up user (can view & edit profile only)
 * - CLIENT: Promoted client (unlocked Project Workspace, deliverables, requests)
 * - ADMIN: Verve AI Labs administrators
 */

export const UserRole = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  CLIENT: 'CLIENT',
  USER: 'USER',
};

export const SUPER_ADMIN_EMAIL = 'admin@verveailabs.com';

/**
 * Normalizes any role string into canonical USER, CLIENT, or ADMIN
 * @param {string} role 
 * @param {string} email 
 * @returns {string} UserRole
 */
export function normalizeRole(role, email) {
  if (email && email.trim().toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase()) {
    return UserRole.ADMIN;
  }
  const r = (role || '').toString().trim().toUpperCase();
  if (r === 'ADMIN' || r === 'SUPER_ADMIN') {
    return UserRole.ADMIN;
  }
  if (r === 'CLIENT') {
    return UserRole.CLIENT;
  }
  return UserRole.USER;
}

/**
 * Returns true if user has CLIENT role privileges
 * @param {Object} user 
 * @returns {boolean}
 */
export function isUserClient(user) {
  if (!user) return false;
  const role = normalizeRole(user.role, user.email);
  return role === UserRole.CLIENT;
}

/**
 * Returns true if user has ADMIN role privileges
 * @param {Object} user 
 * @returns {boolean}
 */
export function isUserAdmin(user) {
  if (!user) return false;
  const role = normalizeRole(user.role, user.email);
  return role === UserRole.ADMIN;
}

/**
 * Returns true if user has access to client project workspace (CLIENT or ADMIN)
 * @param {Object} user 
 * @returns {boolean}
 */
export function hasProjectWorkspaceAccess(user) {
  return isUserClient(user) || isUserAdmin(user);
}
